import { formatMoney, type Currency, type QuoteModeValue } from '@devizy/shared';
import { MinusCircle, Plus, Warning } from '@phosphor-icons/react/dist/ssr';
import * as React from 'react';

import { cn } from '../../lib/cn';
import { AccordionContent, AccordionItem, AccordionTrigger } from '../accordion';
import { Button } from '../button';

import { computeSectionStats, ORPHANS_GROUP_ID } from './group-lines';
import { QuoteLine } from './quote-line';

import type { QuoteDensity } from './density';
import type { QuoteViewExpectation, QuoteViewLabels, QuoteViewLine, QuoteViewMode } from './types';
import type { Icon } from '@phosphor-icons/react';

function SectionDiscrepancyCounter({
  icon: IconComponent,
  count,
  label,
  className,
}: {
  icon: Icon;
  count: number;
  label: string;
  className: string;
}): React.ReactElement | null {
  if (count === 0) return null;
  return (
    <span title={label} className={cn('flex items-center gap-0.5 font-medium', className)}>
      <IconComponent weight="bold" aria-hidden className="h-3 w-3" />
      {count}
      <span className="sr-only">{label}</span>
    </span>
  );
}

export interface QuoteSectionProps {
  parent: QuoteViewExpectation;
  lines: QuoteViewLine[];
  expectations: QuoteViewExpectation[];
  mode: QuoteViewMode;
  density: QuoteDensity;
  quoteMode: QuoteModeValue;
  currency: Currency;
  labels: QuoteViewLabels;
  onLineChange?: (line: QuoteViewLine, patch: Partial<QuoteViewLine>) => void;
  onAddLine?: (parentId: string) => void;
  onRemoveLine?: (line: QuoteViewLine) => void;
}

export function QuoteSection({
  parent,
  lines,
  expectations,
  mode,
  density,
  quoteMode,
  currency,
  labels,
  onLineChange,
  onAddLine,
  onRemoveLine,
}: QuoteSectionProps): React.ReactElement {
  const groupSubtotal = lines.reduce(
    (acc, line) => acc + (line.isProposedForDeletion ? 0 : (line.subtotal ?? 0)),
    0,
  );
  const stats = computeSectionStats(lines);
  const isEdit = mode === 'bidder-edit';

  const header = (
    <div className="flex flex-1 flex-wrap items-center justify-between gap-2 pr-2">
      <span className="font-semibold">{parent.title}</span>
      <span className="flex flex-wrap items-center gap-2 text-xs">
        <SectionDiscrepancyCounter
          icon={Plus}
          count={stats.additionCount}
          label={labels.discrepancy.ADDITION}
          className="text-success"
        />
        <SectionDiscrepancyCounter
          icon={Warning}
          count={stats.modificationCount}
          label={labels.discrepancy.MODIFICATION}
          className="text-warning"
        />
        <SectionDiscrepancyCounter
          icon={MinusCircle}
          count={stats.deletionCount}
          label={labels.discrepancy.DELETION_PROPOSED}
          className="text-destructive"
        />
        {isEdit ? (
          <span className="text-muted-foreground">
            {labels.pricedCount(stats.pricedCount)}
            {stats.toCompleteCount > 0 ? (
              <>
                {' · '}
                <span className="text-warning font-medium">
                  {labels.toCompleteCount(stats.toCompleteCount)}
                </span>
              </>
            ) : null}
          </span>
        ) : null}
        <span className="text-muted-foreground">
          {formatMoney(groupSubtotal, currency)} · {labels.linesCount(lines.length)}
        </span>
      </span>
    </div>
  );

  const body = (
    <div className="space-y-3">
      {lines.length === 0 ? (
        <p className="text-muted-foreground text-xs">{labels.noLines}</p>
      ) : null}
      {lines.map((line) => {
        const expectation =
          line.expectationId !== null
            ? (expectations.find((e) => e.id === line.expectationId) ?? null)
            : null;
        return (
          <QuoteLine
            key={line.id ?? `${parent.id}-${String(line.order)}`}
            mode={mode}
            quoteMode={quoteMode}
            line={line}
            expectation={expectation}
            currency={currency}
            labels={labels}
            onChange={onLineChange ? (patch) => onLineChange(line, patch) : undefined}
            onRemove={onRemoveLine ? () => onRemoveLine(line) : undefined}
          />
        );
      })}
      {onAddLine && parent.id !== ORPHANS_GROUP_ID ? (
        <Button type="button" variant="outline" size="sm" onClick={() => onAddLine(parent.id)}>
          <Plus weight="bold" className="mr-1 h-3.5 w-3.5" />
          {labels.addLine}
        </Button>
      ) : null}
    </div>
  );

  if (density === 'flat') {
    return (
      <section className="border-input rounded-lg border" data-quote-section={parent.id}>
        <div className="border-input flex items-center border-b px-3 py-2.5">{header}</div>
        <div className="p-3">{body}</div>
      </section>
    );
  }

  return (
    <AccordionItem value={parent.id} className="border-input rounded-lg border">
      <AccordionTrigger className="px-3">{header}</AccordionTrigger>
      <AccordionContent className="px-3 pb-3">{body}</AccordionContent>
    </AccordionItem>
  );
}
